import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { JawaWordmark } from "./Logo";
import { JOURNEYS } from "@/data/content";
import { CURRENCY_LIST, useCurrency } from "@/lib/currency";

const NavDropdown = ({ label, items, dark, testid }) => (
  <div className="group relative">
    <button
      data-testid={testid}
      className={`flex items-center gap-1 text-xs font-medium uppercase tracking-[0.2em] transition-colors ${
        dark ? "text-jawa-espresso hover:text-jawa-coral" : "text-jawa-ivory hover:text-jawa-turquoise"
      }`}
    >
      {label} <ChevronDown className="h-3.5 w-3.5" />
    </button>
    <div className="invisible absolute left-0 top-full pt-5 opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
      <div className="grid w-[28rem] grid-cols-1 gap-1 border border-jawa-stone bg-jawa-ivory/95 p-4 shadow-sm backdrop-blur-md">
        {items.map((d) => (
          <Link
            key={d.label}
            to={d.href}
            data-testid={`${testid}-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group/item px-3 py-2.5 transition-colors hover:bg-jawa-stone/60"
          >
            <span className="block font-serif text-lg text-jawa-espresso group-hover/item:text-jawa-coral">
              {d.label}
            </span>
            <span className="block text-[11px] uppercase tracking-[0.15em] text-jawa-espresso/50">{d.note}</span>
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export const CurrencySelector = ({ dark, testid = "currency-selector" }) => {
  const ctx = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  if (!ctx) return null;
  const { currency, setCurrency } = ctx;
  const active = CURRENCY_LIST.find((c) => c.code === currency);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={testid}
        aria-label="Choose display currency"
        className={`flex items-center gap-1 border px-3 py-2 text-xs font-semibold tracking-[0.15em] transition-colors ${
          dark
            ? "border-jawa-espresso/25 text-jawa-espresso hover:border-jawa-coral"
            : "border-jawa-ivory/30 text-jawa-ivory hover:border-jawa-turquoise"
        }`}
      >
        {active?.symbol} {currency} <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-44 border border-jawa-stone bg-jawa-ivory p-1 shadow-sm" data-testid={`${testid}-menu`}>
          {CURRENCY_LIST.map((c) => (
            <button
              key={c.code}
              onClick={() => {
                setCurrency(c.code);
                setOpen(false);
              }}
              data-testid={`${testid}-${c.code.toLowerCase()}`}
              className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-xs transition-colors hover:bg-jawa-stone/60 ${
                c.code === currency ? "font-semibold text-jawa-coral" : "text-jawa-espresso"
              }`}
            >
              <span>{c.label}</span>
              <span className="font-mono">{c.symbol} {c.code}</span>
            </button>
          ))}
          <p className="border-t border-jawa-stone/70 px-3 py-2 text-[10px] leading-relaxed text-jawa-espresso/45">
            Display only — checkout charges naira (₦).
          </p>
        </div>
      )}
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || open;

  const shopLinks = [
    { label: "The Shop", note: "Guides, VIP boxes & curated goods", href: "/shop" },
    { label: "A–Z Guides", note: "Flagship guides & free resources", href: "/guides" },
    { label: "Memberships", note: "Join the JÀWÀ circle", href: "/memberships" },
    { label: "Detty December", note: "The season guide", href: "/detty-december" },
    { label: "Your Downloads", note: "Recover purchases", href: "/orders" },
  ];
  const experienceLinks = [
    { label: "Tours & Experiences", note: "Curated days & nights", href: "/experiences" },
    { label: "Nigeria", note: "36 States + FCT", href: "/destinations/nigeria" },
    { label: "Lagos", note: "Flagship city", href: "/destinations/lagos" },
    { label: "All destinations", note: "Explore Africa", href: "/destinations" },
    { label: "Bespoke Itineraries", note: "Trips planned for you", href: "/services" },
  ];
  const conciergeLinks = [
    { label: "Concierge", note: "Personal assistance", href: "/concierge" },
    { label: "1-2-1 Consultations", note: "Video calls · WAT", href: "/consultations" },
    { label: "VIP Arrival Experience", note: "Boxes & airport pickup", href: "/#vip-arrival" },
    { label: "Emergency Contacts", note: "Verified numbers — free", href: "/emergency" },
  ];
  const aboutLinks = [
    { label: "About JÀWÀ", note: "Our story & approach", href: "/about" },
    ...JOURNEYS.map((j) => ({ label: j.title, note: j.kicker, href: `/journeys/${j.id}` })),
    { label: "Community", note: "The JÀWÀ circle", href: "/community" },
    { label: "FAQ", note: "Questions answered", href: "/faq" },
  ];

  const mobileLinks = [
    { label: "Home", href: "/" },
    ...shopLinks,
    ...experienceLinks,
    ...conciergeLinks,
    ...aboutLinks,
    { label: "Testimonials", href: "/testimonials" },
    { label: "Partner With Us", href: "/partner-with-us" },
    { label: "Member Sign-in", href: "/members" },
  ];

  return (
    <header
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        dark
          ? "border-b border-jawa-stone bg-jawa-ivory/85 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-jawa-nearblack/60 to-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[90rem] items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="JÀWÀ AFRICA home">
          <JawaWordmark dark={dark} />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          <Link
            to="/"
            data-testid="nav-link-home"
            className={`text-xs font-medium uppercase tracking-[0.18em] transition-colors ${
              dark ? "text-jawa-espresso hover:text-jawa-coral" : "text-jawa-ivory hover:text-jawa-turquoise"
            }`}
          >
            Home
          </Link>
          <NavDropdown label="Shop & Guides" items={shopLinks} dark={dark} testid="nav-shop-toggle" />
          <NavDropdown label="Experiences & Stays" items={experienceLinks} dark={dark} testid="nav-experiences-toggle" />
          <NavDropdown label="Concierge & Consultations" items={conciergeLinks} dark={dark} testid="nav-concierge-toggle" />
          <NavDropdown label="About JÀWÀ" items={aboutLinks} dark={dark} testid="nav-about-toggle" />
          <Link
            to="/testimonials"
            data-testid="nav-link-testimonials"
            className={`text-xs font-medium uppercase tracking-[0.18em] transition-colors ${
              dark ? "text-jawa-espresso hover:text-jawa-coral" : "text-jawa-ivory hover:text-jawa-turquoise"
            }`}
          >
            Testimonials
          </Link>
          <Link
            to="/partner-with-us"
            data-testid="nav-link-partner-with-us"
            className={`text-xs font-medium uppercase tracking-[0.18em] transition-colors ${
              dark ? "text-jawa-espresso hover:text-jawa-coral" : "text-jawa-ivory hover:text-jawa-turquoise"
            }`}
          >
            Partner With Us
          </Link>
          <CurrencySelector dark={dark} />
          <Link
            to="/memberships"
            data-testid="nav-join-cta"
            className="bg-jawa-coral px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-espresso"
          >
            Join JÀWÀ
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <CurrencySelector dark={dark} testid="currency-selector-mobile" />
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            onClick={() => setOpen(!open)}
            className={dark ? "text-jawa-espresso" : "text-jawa-ivory"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[80vh] overflow-y-auto border-t border-jawa-stone bg-jawa-ivory/95 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {mobileLinks.map((l) => (
                <Link
                  key={l.label}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="border-b border-jawa-stone/60 py-3 font-serif text-xl text-jawa-espresso transition-colors hover:text-jawa-coral"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
